import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/lib/property";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CaretLeftIcon, CaretRightIcon, SparkleIcon } from "@phosphor-icons/react";

const DAY = 86400000;
const fmt = (d) => new Date(d).toISOString().slice(0, 10);
const addDays = (d, n) => fmt(new Date(new Date(d).getTime() + n * DAY));
const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

export default function RateCalendar() {
  const { activeId, active } = useProperty();
  const [start, setStart] = useState(fmt(new Date()));
  const days = 30;
  const [data, setData] = useState({ dates: [], room_types: [], overrides: [] });
  const [selected, setSelected] = useState([]); // [{room_type, date}]
  const [dialog, setDialog] = useState(false);
  const [newRate, setNewRate] = useState(0);

  const end = addDays(start, days);

  const load = () => {
    if (!activeId) return;
    api.get("/rates", { params: { property_id: activeId, start, end } }).then((r) => setData(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeId, start]);

  const overrideMap = useMemo(() => {
    const m = {};
    for (const o of data.overrides) m[`${o.room_type}|${o.date}`] = o.rate;
    return m;
  }, [data.overrides]);

  const isSelected = (rt, d) => selected.some((s) => s.room_type === rt && s.date === d);
  const toggle = (rt, d) => {
    setSelected((prev) => isSelected(rt, d)
      ? prev.filter((s) => !(s.room_type === rt && s.date === d))
      : [...prev, { room_type: rt, date: d }]);
  };

  const openSetRate = () => {
    if (selected.length === 0) return toast.error("Select at least one cell");
    // Prefill with first cell's current rate
    const first = selected[0];
    const currentBase = data.room_types.find((t) => t.name === first.room_type)?.base_rate || 0;
    setNewRate(overrideMap[`${first.room_type}|${first.date}`] ?? currentBase);
    setDialog(true);
  };

  const applyRate = async () => {
    // Group by room_type
    const byType = {};
    selected.forEach((s) => (byType[s.room_type] = [...(byType[s.room_type] || []), s.date]));
    try {
      for (const [rt, dates] of Object.entries(byType)) {
        await api.put("/rates/bulk", { property_id: activeId, room_type: rt, dates, rate: Number(newRate) });
      }
      toast.success(`Updated ${selected.length} cells`);
      setDialog(false);
      setSelected([]);
      load();
    } catch (e) { toast.error("Failed to update"); }
  };

  const resetSelected = async () => {
    if (!selected.length) return;
    try {
      for (const s of selected) {
        await api.delete("/rates", { params: { property_id: activeId, room_type: s.room_type, date: s.date } });
      }
      toast.success("Reset to base rate");
      setSelected([]);
      load();
    } catch { toast.error("Failed"); }
  };

  const columnWidth = 52;

  // Preset dialog
  const [presetOpen, setPresetOpen] = useState(false);
  const [preset, setPreset] = useState({ start: fmt(new Date()), end: addDays(fmt(new Date()), 7), multiplier: 1.5, weekend_only: false, room_types: [], mode: "multiplier", rate: 6000 });
  const applyPreset = async () => {
    try {
      const body = {
        property_id: activeId,
        start: preset.start,
        end: preset.end,
        weekend_only: preset.weekend_only,
        room_types: preset.room_types,
      };
      if (preset.mode === "multiplier") body.multiplier = Number(preset.multiplier);
      else body.rate = Number(preset.rate);
      const r = await api.post("/rates/preset", body);
      toast.success(`Applied to ${r.data.cells_updated} cells`);
      setPresetOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Preset failed"); }
  };

  const seasonPresets = [
    { name: "Weekend surge", multiplier: 1.3, weekend_only: true },
    { name: "Dussehra week", multiplier: 1.5, weekend_only: false },
    { name: "Diwali peak", multiplier: 1.75, weekend_only: false },
    { name: "New Year peak", multiplier: 2.0, weekend_only: false },
  ];

  return (
    <div>
      <PageHeader
        overline="Revenue"
        title="Rate Calendar"
        description={active ? `${active.name} · click cells to select, then edit rates in bulk` : "Nightly rates by room type"}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => setStart(addDays(start, -14))}><CaretLeftIcon size={14} /></Button>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" data-testid="rate-date" />
            <Button variant="outline" size="icon" onClick={() => setStart(addDays(start, 14))}><CaretRightIcon size={14} /></Button>
            <Button variant="outline" size="sm" onClick={() => setPresetOpen(true)} data-testid="rate-preset-btn"><SparkleIcon size={14} className="mr-1" />Presets</Button>
            {selected.length > 0 && (
              <>
                <span className="text-xs text-muted-foreground ml-2">{selected.length} selected</span>
                <Button variant="outline" size="sm" onClick={resetSelected} data-testid="rate-reset-btn">Reset to base</Button>
                <Button size="sm" onClick={openSetRate} data-testid="rate-edit-btn">Set rate</Button>
              </>
            )}
          </div>
        }
      />

      <Card className="overflow-x-auto">
        <div className="min-w-max">
          <div className="flex sticky top-0 z-10 bg-card border-b border-border">
            <div className="w-44 shrink-0 p-3 label-overline border-r border-border">Room type</div>
            {data.dates.map((d) => {
              const dt = new Date(d);
              const isWknd = [0, 6].includes(dt.getDay());
              return (
                <div key={d} style={{ width: columnWidth }} className={`shrink-0 text-center py-2 border-r border-border/60 text-xs ${isWknd ? "bg-muted/50" : ""}`}>
                  <div className="uppercase text-[10px] tracking-widest text-muted-foreground">{dt.toLocaleDateString("en-IN", { weekday: "short" })}</div>
                  <div className="font-mono-plex">{d.slice(8, 10)}</div>
                </div>
              );
            })}
          </div>

          {data.room_types.map((rt) => (
            <div key={rt.name} className="flex border-b border-border/40">
              <div className="w-44 shrink-0 p-3 border-r border-border sticky left-0 bg-card">
                <p className="font-medium text-sm">{rt.name}</p>
                <p className="text-[11px] text-muted-foreground">Base {inr(rt.base_rate)}</p>
              </div>
              {data.dates.map((d) => {
                const key = `${rt.name}|${d}`;
                const rate = overrideMap[key];
                const sel = isSelected(rt.name, d);
                const isWknd = [0, 6].includes(new Date(d).getDay());
                const displayRate = rate ?? rt.base_rate;
                return (
                  <button
                    key={key}
                    onClick={() => toggle(rt.name, d)}
                    className={`shrink-0 border-r border-border/40 text-center py-3 text-xs font-mono-plex ${
                      sel ? "bg-primary/20 outline outline-2 outline-primary" : rate ? "bg-secondary/10" : isWknd ? "bg-muted/40" : ""
                    } hover:bg-primary/10`}
                    style={{ width: columnWidth, height: 56 }}
                    data-testid={`rate-cell-${rt.name}-${d}`}
                  >
                    ₹{displayRate}
                    {rate && <div className="text-[9px] text-secondary uppercase tracking-widest mt-0.5">custom</div>}
                  </button>
                );
              })}
            </div>
          ))}
          {data.room_types.length === 0 && <div className="p-10 text-center text-muted-foreground text-sm">No room types.</div>}
        </div>
      </Card>

      <div className="mt-4 flex gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-secondary/10 border border-secondary/40" /> Custom rate</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-muted/40 border border-border" /> Weekend</span>
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Set rate for {selected.length} cell{selected.length > 1 && "s"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>New rate (₹ per night)</Label>
              <Input type="number" value={newRate} onChange={(e) => setNewRate(e.target.value)} data-testid="rate-new-value" />
            </div>
            <p className="text-xs text-muted-foreground">This overrides the base room-type rate on the selected dates. New reservations booked over these dates will use this rate.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
            <Button onClick={applyRate} data-testid="rate-apply-btn">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={presetOpen} onOpenChange={setPresetOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Seasonal preset</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="label-overline mb-2">Quick presets</p>
              <div className="flex flex-wrap gap-2">
                {seasonPresets.map((p) => (
                  <Button key={p.name} size="sm" variant="outline" onClick={() => setPreset({ ...preset, multiplier: p.multiplier, weekend_only: p.weekend_only, mode: "multiplier" })} data-testid={`rate-preset-${p.name.replace(/ /g,'-').toLowerCase()}`}>
                    {p.name}
                  </Button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start</Label><Input type="date" value={preset.start} onChange={(e) => setPreset({ ...preset, start: e.target.value })} data-testid="preset-start" /></div>
              <div><Label>End</Label><Input type="date" value={preset.end} onChange={(e) => setPreset({ ...preset, end: e.target.value })} data-testid="preset-end" /></div>
            </div>
            <div>
              <Label>Mode</Label>
              <Select value={preset.mode} onValueChange={(v) => setPreset({ ...preset, mode: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="multiplier">Multiplier on base rate</SelectItem>
                  <SelectItem value="fixed">Fixed rate (₹)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {preset.mode === "multiplier" ? (
              <div><Label>Multiplier (e.g. 1.5 = +50%)</Label><Input type="number" step="0.05" value={preset.multiplier} onChange={(e) => setPreset({ ...preset, multiplier: e.target.value })} data-testid="preset-multiplier" /></div>
            ) : (
              <div><Label>Fixed rate (₹)</Label><Input type="number" value={preset.rate} onChange={(e) => setPreset({ ...preset, rate: e.target.value })} /></div>
            )}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={preset.weekend_only} onChange={(e) => setPreset({ ...preset, weekend_only: e.target.checked })} data-testid="preset-weekend-only" />
              Apply only on Fri/Sat/Sun
            </label>
            <div>
              <p className="label-overline mb-1">Room types (leave empty = all)</p>
              <div className="flex flex-wrap gap-2">
                {data.room_types.map((t) => {
                  const selected = preset.room_types.includes(t.name);
                  return (
                    <button
                      key={t.name}
                      type="button"
                      onClick={() => setPreset({ ...preset, room_types: selected ? preset.room_types.filter((x) => x !== t.name) : [...preset.room_types, t.name] })}
                      className={`text-xs px-3 py-1.5 rounded-full border ${selected ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}
                    >
                      {t.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPresetOpen(false)}>Cancel</Button>
            <Button onClick={applyPreset} data-testid="preset-apply-btn">Apply preset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
